import type { AxiosError, AxiosResponse } from 'axios';
import * as axios from 'axios';
import qs from 'qs';

/**
 * Subset of AxiosRequestConfig
 */
type RequestConfig<TData = unknown> = {
  baseURL?: string;
  url?: string;
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS';
  params?: unknown;
  data?: TData | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: axios.AxiosRequestConfig['headers'];
};

/**
 * Subset of AxiosResponse
 */
type ResponseConfig<TData = unknown> = {
  data: TData & { newBranchId?: string };
  status: number;
  statusText: string;
  headers: AxiosResponse['headers'];
};

type ResponseErrorConfig<TError = unknown> = {
  errorResponse: { data: AxiosError<TError>; status: number };
  userError?: string;
  userHelp?: string;
};

// Configuration storage
let config: Partial<RequestConfig> = {};

const getConfig = () => config;
const setConfig = (newConfig: RequestConfig) => {
  config = { ...config, ...newConfig };
  return config;
};

const axiosInstance = axios.default.create({
  paramsSerializer: params => qs.stringify(params),
});

const client = async <TData, _TError = unknown, TVariables = unknown>(
  requestConfig: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const mergedConfig = { ...config, ...requestConfig };
  const response = await axiosInstance.request(mergedConfig);

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  };
};

// Attach configuration methods to client
client.getConfig = getConfig;
client.setConfig = setConfig;

type Client = typeof client;

export {
  axiosInstance,
  client,
  client as default,
  getConfig,
  setConfig,
  type Client,
  type RequestConfig,
  type ResponseConfig,
  type ResponseErrorConfig,
};

export function getNewBranchIdAfterFork(response: object): string | undefined {
  return (response as { newBranchId?: string }).newBranchId;
}
